import { z } from "zod";
import { useTranslation } from "react-i18next";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";

import { Diff } from "@/features/FormCard/Diff";

import { CheckboxField, InputField } from "@/features/FormCard/Fields";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { FormActionsUpdate } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";
import { Label } from "@radix-ui/react-label";

const FormSchema = z.object({
  client: z.string().nullable().optional(),
  page_name: z.string().nullable().optional(),
  tableau_view: z.string().nullable().optional(),
  tableau_workbook_id: z.string().nullable().optional(),
  pdf_report_display: z.boolean().nullable().optional(),
});

export function K2PdfPageSelectionEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation(["common", "tables"]);

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    isDeleting,
    changedFields,
    handleDelete,
  } = useEditForm({
    id,
    client,
    table_name,
    FormSchema,
  });

  return (
    <>
      <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
        <div className="flex justify-center w-full">
          <Card className="p-4 bg-white w-full ">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <CardHeader className="text-lg font-bold text-gray-700">
                  {t(`${table_name}.title`, {
                    ns: "tables",
                    defaultValue: "PDF page selection",
                  })}{" "}
                  {id}
                </CardHeader>
                <Divider />
                <CardBody className="flex flex-col gap-4">
                  <Label>{t("sections.selection_info")}</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      name="client"
                      label={t(`${table_name}.columns.client`, {
                        ns: "tables",
                        defaultValue: "Client",
                      })}
                      control={form.control}
                    />
                    <InputField
                      name="page_name"
                      label={t(`${table_name}.columns.page_name`, {
                        ns: "tables",
                        defaultValue: "Page name",
                      })}
                      control={form.control}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      name="tableau_view"
                      label={t(`${table_name}.columns.tableau_view`, {
                        ns: "tables",
                        defaultValue: "Tableau view",
                      })}
                      control={form.control}
                    />
                    <InputField
                      name="tableau_workbook_id"
                      label={t(`${table_name}.columns.tableau_workbook_id`, {
                        ns: "tables",
                        defaultValue: "Tableau workbook ID",
                      })}
                      control={form.control}
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    <CheckboxField
                      name="pdf_report_display"
                      label={t(`${table_name}.columns.pdf_report_display`, {
                        ns: "tables",
                        defaultValue: "PDF report display",
                      })}
                      control={form.control}
                    />
                  </div>
                </CardBody>
                <CardFooter>
                  <FormActionsUpdate
                    onReset={handleReset}
                    onDelete={openDeleteModal}
                    isDirty={form.formState.isDirty}
                    elementId={id}
                    client={client}
                    allowDelete={false}
                  />
                </CardFooter>
              </form>
            </Form>
          </Card>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
            children={
              changedFields
                ? Diff(changedFields, [], undefined, table_name)
                : t("no_changes")
            }
          />

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </div>
      </div>
    </>
  );
}
